import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { IconButton } from '../components/Button';
import Celebration from '../components/layout/Celebration';
import { trackUiModalClosed, trackUiModalOpened } from '../analytics';
import styles from '../styles/layout/Modal.module.css';
import { useGoogleCmp } from '../utils/googleCmp';

const ModalContext = createContext();

/**
 * Hook to access the global modal functionality
 * 
 * Usage:
 * const { openModal, closeModal } = useGlobalModal();
 * 
 * // With header (title provided):
 * openModal({ title: 'My Title', content: <MyContent /> });
 * 
 * // Without header (no title = no header):
 * openModal({ content: <MyContentWithOwnHeader /> });
 * 
 * // Explicit override:
 * openModal({ title: 'Title', content: <X />, showHeader: false }); // force no header
 * openModal({ content: <X />, showHeader: true }); // force header (close button only)
 *
 * Gameplay dialogs (desktop only):
 * openModal({ title: 'Istruzioni', content: <X />, alignToGame: true });
 */
export const useGlobalModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useGlobalModal must be used within a ModalProvider');
  }
  return context;
};

/**
 * Reusable Modal Header component
 * Use this within your modal content when showHeader: false
 * 
 * Usage:
 * const { closeModal } = useGlobalModal();
 * <ModalHeader title="My Title" onClose={closeModal} />
 */
export const ModalHeader = ({ title, onClose, className = '' }) => {
  return (
    <div className={`${styles.header} ${className}`}>
      {title && (
        <h2 className={styles.title}>
          {title}
        </h2>
      )}
      {onClose && (
        <IconButton 
          iconClassName="fas fa-times" 
          onClick={() => onClose('header_close_button')} 
          className={styles.closeButton}
          title="Chiudi"
        />
      )}
    </div>
  );
};

/**
 * Modal component rendered by the provider
 * Renders inline (not via portal) to maintain Router context
 */
const GlobalModal = ({
  isOpen,
  suspended,
  onClose,
  title,
  showHeader,
  containerClassName,
  alignToGame,
  celebrationType,
  celebrationKey,
  trapFocus,
  children,
}) => {
  const modalRef = useRef(null);
  const titleRef = useRef(null);
  const previousActiveElement = useRef(null);
  const suspendedRef = useRef(suspended);
  suspendedRef.current = suspended;
  const [isInteractable, setIsInteractable] = useState(false);

  // Handle escape key
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && onClose) {
      onClose('escape_key');
    }
    if (e.key === 'Tab' && trapFocus && modalRef.current) {
      const focusable = Array.from(modalRef.current.querySelectorAll(
        'button:not(:disabled), a[href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]',
      )).filter((element) => element.tabIndex >= 0 && !element.closest('[hidden]') && element.getAttribute('aria-disabled') !== 'true');
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (!first) {
        e.preventDefault();
        modalRef.current.focus();
      } else if (!focusable.includes(active) || (e.shiftKey ? active === first : active === last)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      }
    }
  }, [onClose, trapFocus]);

  // Handle overlay click
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget && onClose) {
      onClose('overlay_click');
    }
  }, [onClose]);

  // Focus management and scroll lock
  useEffect(() => {
    if (isOpen && !suspended) {
      setIsInteractable(false);
      previousActiveElement.current = document.activeElement;
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', handleKeyDown);
      
      if (modalRef.current) {
        (trapFocus && titleRef.current ? titleRef.current : modalRef.current).focus();
      }

      return () => {
        document.body.style.overflow = originalOverflow;
        document.removeEventListener('keydown', handleKeyDown);
        if (!suspendedRef.current && previousActiveElement.current && typeof previousActiveElement.current.focus === 'function') {
          previousActiveElement.current.focus();
        }
      };
    }
  }, [isOpen, suspended, handleKeyDown, trapFocus]);

  useEffect(() => {
    if (!isOpen || suspended) {
      setIsInteractable(false);
      return undefined;
    }

    // Prevent touch/click-through from the opening gesture during slide/fade-in.
    const timerId = setTimeout(() => {
      setIsInteractable(true);
    }, 260);

    return () => {
      clearTimeout(timerId);
    };
  }, [isOpen, suspended]);

  if (!isOpen) return null;

  return (
    <div 
      className={`${styles.overlay} ${alignToGame ? styles.gameAligned : ''}`}
      onClick={handleOverlayClick}
      onClickCapture={(event) => {
        if (trapFocus && !isInteractable) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
      aria-modal="true"
      hidden={suspended}
      inert={suspended}
      role="dialog"
      aria-labelledby={title ? 'global-modal-title' : undefined}
      style={{ position: 'fixed', zIndex: 10000, display: suspended ? 'none' : undefined }}
    >
      {celebrationType ? (
        <Celebration
          key={celebrationKey}
          trigger={true}
          type={celebrationType}
        />
      ) : null}
      <div 
        ref={modalRef}
        className={`${styles.container} ${containerClassName || ''}`}
        tabIndex={-1}
        style={{ pointerEvents: isInteractable ? 'auto' : 'none' }}
      >
        {showHeader && (
          <div className={styles.header}>
            {title && (
              <h2 ref={titleRef} id="global-modal-title" className={styles.title} tabIndex={trapFocus ? -1 : undefined}>
                {title}
              </h2>
            )}
            <IconButton 
              iconClassName="fas fa-times" 
              onClick={() => onClose('header_close_button')} 
              className={styles.closeButton}
              title="Chiudi"
            />
          </div>
        )}
        <div className={styles.content}>
          {children}
        </div>
      </div>
    </div>
  );
};

/**
 * Provider component that wraps the app and provides global modal functionality
 */
export const ModalProvider = ({ children }) => {
  const cmp = useGoogleCmp();
  const consentBlocked = cmp.isBusy || cmp.siteConsentOpen;
  const [isOpen, setIsOpen] = useState(false);
  const [modalData, setModalData] = useState({
    title: '',
    content: null,
    showHeader: false,
    containerClassName: '',
    alignToGame: false,
    celebrationType: null,
    celebrationKey: 0,
    trapFocus: false,
  });
  const modalOnCloseRef = useRef(null);
  const modalAnalyticsRef = useRef({});
  const previousIsOpenRef = useRef(false);
  const lastCloseReasonRef = useRef('programmatic');
  const clearModalDataTimeoutRef = useRef(null);
  const celebrationSequenceRef = useRef(0);

  const clearPendingModalDataReset = useCallback(() => {
    if (!clearModalDataTimeoutRef.current) return;
    clearTimeout(clearModalDataTimeoutRef.current);
    clearModalDataTimeoutRef.current = null;
  }, []);

  const openModal = useCallback(({
    title = '',
    content = null,
    showHeader,
    onClose,
    containerClassName = '',
    alignToGame = false,
    celebrationType = null,
    trapFocus = false,
    analytics = {},
  }) => {
    // Default showHeader to true if title is provided, false otherwise
    const shouldShowHeader = showHeader !== undefined ? showHeader : !!title;
    const shouldIncrementCelebrationKey = !isOpen && celebrationType !== null;
    if (shouldIncrementCelebrationKey) {
      celebrationSequenceRef.current += 1;
    }
    clearPendingModalDataReset();
    setModalData((previous) => {
      if (
        previous.title === title
        && previous.content === content
        && previous.showHeader === shouldShowHeader
        && previous.containerClassName === containerClassName
        && previous.alignToGame === alignToGame
        && previous.celebrationType === celebrationType
        && previous.trapFocus === trapFocus
      ) {
        return previous;
      }

      return {
        title,
        content,
        showHeader: shouldShowHeader,
        containerClassName,
        alignToGame,
        celebrationType,
        trapFocus,
        celebrationKey: shouldIncrementCelebrationKey
          ? celebrationSequenceRef.current
          : 0,
      };
    });
    modalOnCloseRef.current = onClose || null;
    modalAnalyticsRef.current = {
      source_surface: analytics.source_surface || 'global_modal',
      modal_name: analytics.modal_name || title || null,
      trigger: analytics.trigger || null,
      game_slug: analytics.game_slug || null,
    };
    setIsOpen(true);
  }, [clearPendingModalDataReset, isOpen]);

  const closeModal = useCallback((reason = 'programmatic') => {
    lastCloseReasonRef.current = reason;
    // Call custom onClose handler if provided (before closing)
    if (modalOnCloseRef.current && typeof modalOnCloseRef.current === 'function') {
      modalOnCloseRef.current(reason);
      modalOnCloseRef.current = null;
    }
    
    setIsOpen(false);
    clearPendingModalDataReset();
    // Clear content after a short delay to allow close animation
    clearModalDataTimeoutRef.current = setTimeout(() => {
      setModalData({
        title: '',
        content: null,
        showHeader: true,
        containerClassName: '',
        alignToGame: false,
        celebrationType: null,
        celebrationKey: 0,
        trapFocus: false,
      });
      clearModalDataTimeoutRef.current = null;
    }, 300);
  }, [clearPendingModalDataReset]);

  useEffect(() => () => {
    clearPendingModalDataReset();
  }, [clearPendingModalDataReset]);

  useEffect(() => {
    if (isOpen && !previousIsOpenRef.current) {
      const analytics = modalAnalyticsRef.current || {};
      trackUiModalOpened({
        source_surface: analytics.source_surface || 'global_modal',
        surface: 'global_modal',
        modal_name: analytics.modal_name || modalData.title || 'untitled_modal',
        trigger: analytics.trigger || null,
        game_slug: analytics.game_slug || null,
      });
    } else if (!isOpen && previousIsOpenRef.current) {
      const analytics = modalAnalyticsRef.current || {};
      trackUiModalClosed({
        source_surface: analytics.source_surface || 'global_modal',
        surface: 'global_modal',
        modal_name: analytics.modal_name || modalData.title || 'untitled_modal',
        trigger: lastCloseReasonRef.current || 'unknown',
        game_slug: analytics.game_slug || null,
      });
    }

    previousIsOpenRef.current = isOpen;
  }, [isOpen, modalData.title]);

  const value = {
    openModal,
    closeModal,
    isOpen: isOpen || consentBlocked,
    modalName: isOpen ? modalAnalyticsRef.current.modal_name : null,
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
      <GlobalModal 
        isOpen={isOpen} 
        suspended={consentBlocked}
        onClose={closeModal} 
        title={modalData.title}
        showHeader={modalData.showHeader}
        containerClassName={modalData.containerClassName}
        alignToGame={modalData.alignToGame}
        celebrationType={modalData.celebrationType}
        celebrationKey={modalData.celebrationKey}
        trapFocus={modalData.trapFocus}
      >
        {modalData.content}
      </GlobalModal>
    </ModalContext.Provider>
  );
};

export default ModalContext;
